import { useState, type FormEvent } from "react";
import { ArrowRight, Eye, EyeOff, LockKeyhole } from "lucide-react";
import { loginInputSchema, type AuthSessionView } from "@ubisafe/shared";
import { BrandLogo } from "../components/BrandLogo";
import { api, ApiClientError } from "../lib/api";

interface LoginPageProps {
  onAuthenticated: (session: AuthSessionView) => void;
  onForgotPassword: () => void;
}

export function LoginPage({ onAuthenticated, onForgotPassword }: LoginPageProps) {
  const [prefix, setPrefix] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = loginInputSchema.safeParse({ prefix, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Revise os dados informados.");
      return;
    }

    setLoading(true);
    try {
      const session = await api.login(parsed.data);
      onAuthenticated(session);
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível entrar no sistema.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <section className="auth-hero">
        <BrandLogo />
        <div className="auth-hero__content">
          <span className="eyebrow">Ambiente interno</span>
          <h1>Visibilidade e controle para todos os tenants.</h1>
          <p>Acesse os fluxos de Clientes, POCs e administração com as permissões da sua função.</p>
        </div>
        <div className="auth-hero__glow" aria-hidden="true" />
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <div className="auth-card__icon" aria-hidden="true"><LockKeyhole /></div>
          <span className="eyebrow eyebrow--dark">Acesso seguro</span>
          <h2>Entrar no sistema</h2>
          <p className="form-intro">Use sua conta corporativa UbiSafe.</p>

          <form onSubmit={(event) => void submit(event)} noValidate>
            <label htmlFor="login-prefix">Usuário</label>
            <div className="suffix-input">
              <input
                id="login-prefix"
                name="username"
                value={prefix}
                onChange={(event) => setPrefix(event.target.value.toLowerCase().replace("@", ""))}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="seu.nome"
                maxLength={64}
                required
              />
              <span>@ubisafe.com.br</span>
            </div>

            <label htmlFor="login-password">Senha</label>
            <div className="password-input">
              <input
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <button type="button" aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} onClick={() => setShowPassword((value) => !value)}>
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            {error && <div className="form-alert form-alert--error" role="alert">{error}</div>}

            <button className="primary-button primary-button--full" type="submit" disabled={loading}>
              <span>{loading ? "Entrando…" : "Entrar"}</span>
              {!loading && <ArrowRight aria-hidden="true" />}
            </button>

            <button className="link-button" type="button" onClick={onForgotPassword}>
              Esqueci minha senha
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
